import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

// PrimeNG Imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { MenuModule } from 'primeng/menu';
import { SplitButtonModule } from 'primeng/splitbutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { DrawerModule } from 'primeng/drawer';
import { PanelModule } from 'primeng/panel';
import { AccordionModule } from 'primeng/accordion';
import { GalleriaModule } from 'primeng/galleria';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';

// PrimeNG Services
import { MessageService, ConfirmationService } from 'primeng/api';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';

// Local Imports
import { SessionManagerService } from '../services/session-manager.service';
import { MediaService } from '../services/media.service';
import { DownloadService } from '../services/download.service';
import { Session, SessionFilter, SessionStats, SessionCreateResponse, MediaFile, Composition } from '../models/session.model';
import { LayoutComponent } from '../layout/layout.component';
import { SessionCreateDialogComponent } from './session-create-dialog/session-create-dialog.component';
import { MediaGalleryComponent, MediaItem } from '../components/media-gallery/media-gallery.component';
import { GuestLinkDialogComponent, GuestLinkSession } from '../components/guest-link-dialog/guest-link-dialog.component';
import { SessionLogsDialogComponent } from '../components/session-logs-dialog/session-logs-dialog.component';

@Component({
    selector: 'app-session-manager',
    templateUrl: './session-manager.component.html',
    styleUrls: ['./session-manager.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        LayoutComponent,
        SessionCreateDialogComponent,
        MediaGalleryComponent,
        GuestLinkDialogComponent,
        SessionLogsDialogComponent,
        // PrimeNG Modules
        TableModule,
        ButtonModule,
        InputTextModule,
        InputGroupModule,
        DatePickerModule,
        SelectModule,
        DialogModule,
        ConfirmDialogModule,
        ToastModule,
        ToolbarModule,
        TagModule,
        BadgeModule,
        MenuModule,
        SplitButtonModule,
        SelectButtonModule,
        ProgressSpinnerModule,
        CardModule,
        ChipModule,
        DrawerModule,
        PanelModule,
        AccordionModule,
        GalleriaModule,
        TooltipModule,
        CheckboxModule
    ],
    providers: [MessageService, ConfirmationService]
})
export class SessionManagerComponent implements OnInit, OnDestroy {
  // Data properties
  sessions: Session[] = [];
  selectedSessions: Session[] = [];
  stats: SessionStats | null = null;
  currentSession: Session | null = null;

  // UI State
  loading = false;
  showCreateDialog = false;
  showMediaDialog = false;
  showGuestLinkDialog = false;
  showStatsDialog = false;
  showLogsDialog = false;
  
  // Filter and search
  filter: SessionFilter = {};
  globalFilterValue = '';
  
  // Media management
  mediaFiles: MediaFile[] = [];
  mediaItems: MediaItem[] = [];
  compositions: Composition[] = [];
  mediaLoading = false;
  
  // Media grid controls
  mediaGridSize: 'small' | 'medium' | 'large' = 'medium';
  
  // Global search
  globalSearchValue = '';
  
  // Photo section controls
  photoSelectAll = false;
  selectedPhotoItems: Set<string> = new Set();
  photoSortBy: 'date' | 'name' | 'size' = 'date';
  photoSortOrder: 'asc' | 'desc' = 'desc';
  filteredPhotoItems: MediaItem[] = [];
  
  // Video section controls
  videoSelectAll = false;
  selectedVideoItems: Set<string> = new Set();
  videoSortBy: 'date' | 'name' | 'size' = 'date';
  videoSortOrder: 'asc' | 'desc' = 'desc';
  filteredVideoItems: MediaItem[] = [];
  
  // Unified media type controls
  selectedMediaType: 'photos' | 'videos' = 'photos';
  mediaTypeOptions = [
    { label: 'Photos', icon: 'pi pi-image', value: 'photos' },
    { label: 'Videos', icon: 'pi pi-video', value: 'videos' }
  ];
  
  // Sort options shared by both photo and video sections
  sortFieldOptions = [
    { label: 'Date', value: 'date' },
    { label: 'Name', value: 'name' },
    { label: 'Size', value: 'size' }
  ];
  
  sortOrderOptions = [
    { label: 'Asc', value: 'asc', icon: 'pi pi-sort-amount-up' },
    { label: 'Desc', value: 'desc', icon: 'pi pi-sort-amount-down' }
  ];
  
  // Guest Link Dialog
  guestLinkSession: GuestLinkSession | null = null;

  // Menu items
  actionItems: MenuItem[] = [];
  bulkActionItems: MenuItem[] = [];
  photoBatchMenuItems: MenuItem[] = [];
  videoBatchMenuItems: MenuItem[] = [];

  // Menu reference
  @ViewChild('actionMenu') actionMenu!: Menu;

  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private sessionService: SessionManagerService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private mediaService: MediaService,
    private downloadService: DownloadService
  ) {
    this.setupMenuItems();
  }

  ngOnInit() {
    this.loadSessions();
    this.loadStats();
    this.setupPhotoBatchMenuItems();
    this.setupVideoBatchMenuItems();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupMenuItems() {
    this.actionItems = [
      {
        label: 'View Details',
        icon: 'pi pi-eye',
        command: () => this.viewSessionDetails(this.currentSession!)
      },
      {
        label: 'View Media',
        icon: 'pi pi-images',
        command: () => this.viewSessionMedia(this.currentSession!)
      },
      {
        label: 'View Session Logs',
        icon: 'pi pi-history',
        command: () => this.viewSessionLogs(this.currentSession!)
      },
      {
        label: 'Statistics',
        icon: 'pi pi-chart-bar',
        command: () => this.showStatistics()
      },
      {
        separator: true
      },
      {
        label: 'Copy Host URL',
        icon: 'pi pi-copy',
        command: () => this.copyHostUrl(this.currentSession!)
      },
      {
        label: 'Copy Guest URL',
        icon: 'pi pi-copy',
        command: () => this.copyGuestUrl(this.currentSession!)
      },
      {
        label: 'Download All Media',
        icon: 'pi pi-download',
        command: () => this.downloadSessionMedia(this.currentSession!)
      },
      {
        separator: true
      },
      {
        label: 'Re-Process Composition',
        icon: 'pi pi-refresh',
        command: () => this.reprocessComposition(this.currentSession!)
      },
      {
        label: 'Regenerate URLs',
        icon: 'pi pi-sync',
        command: () => this.regenerateSessionUrls(this.currentSession!)
      },
      {
        separator: true
      },
      {
        label: 'Delete Session',
        icon: 'pi pi-trash',
        styleClass: 'text-red-500',
        command: () => this.deleteSession(this.currentSession!)
      }
    ];

    this.bulkActionItems = [
      {
        label: 'Download Selected',
        icon: 'pi pi-download',
        command: () => this.bulkDownload()
      },
      {
        label: 'Delete Selected',
        icon: 'pi pi-trash',
        styleClass: 'text-red-500',
        command: () => this.bulkDelete()
      }
    ];
  }

  // Data loading methods
  loadSessions() {
    this.loading = true;
    this.sessionService.getAllSessions(this.filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sessions) => {
          this.sessions = sessions;
          this.loading = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load sessions'
          });
          this.loading = false;
        }
      });
  }

  loadStats() {
    this.sessionService.getSessionStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.stats = stats;
        },
        error: (error) => {
          console.error('Failed to load statistics:', error);
        }
      });
  }

  // Filter and search methods
  applyGlobalFilter(event: any, dt: any) {
    this.globalFilterValue = event.target.value;
    dt.filterGlobal(event.target.value, 'contains');
  }

  clearFilters(dt: any) {
    dt.clear();
    this.globalFilterValue = '';
    this.filter = {};
  }

  applyDateFilter() {
    this.loadSessions();
  }

  // Session management methods
  createSession() {
    this.currentSession = null;
    this.showCreateDialog = true;
  }

  startSession(session: Session) {
    if (session.hostConnectUrl) {
      // Open the host connect URL in a new tab
      window.open(session.hostConnectUrl, '_blank', 'noopener,noreferrer');
      
      this.messageService.add({
        severity: 'success',
        summary: 'Session Started',
        detail: `Opening session ${session.sessionId} for host...`
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'No URL Available',
        detail: 'Host connect URL is not available for this session'
      });
    }
  }

  copyHostUrl(session: Session) {
    if (session.hostConnectUrl) {
      navigator.clipboard.writeText(session.hostConnectUrl).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Host URL copied to clipboard'
        });
      });
    }
  }

  async openGuestLinkDialog(session: Session) {
    this.currentSession = session;

    if (session.guestConnectUrl) {
      this.guestLinkSession = {
        sessionId: session.sessionId,
        guestUserIdentity: session.guestUserIdentity,
        guestConnectUrl: session.guestConnectUrl
      };

      this.showGuestLinkDialog = true;
    }
  }

  copyGuestUrl(session: Session) {
    if (session.guestConnectUrl) {
      navigator.clipboard.writeText(session.guestConnectUrl).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Guest URL copied to clipboard'
        });
      });
    }
  }

  viewSessionDetails(session: Session) {
    // Implementation for viewing detailed session information
    this.messageService.add({
      severity: 'info',
      summary: 'Session Details',
      detail: `Viewing details for session: ${session.sessionId}`
    });
  }

  deleteSession(session: Session) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete session "${session.sessionId}"? This action cannot be undone.`,
      header: 'Delete Session',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.sessionService.deleteSession(session.sessionId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Session deleted successfully'
              });
              this.loadSessions();
              this.loadStats();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete session'
              });
            }
          });
      }
    });
  }

  reprocessComposition(session: Session) {
    this.confirmationService.confirm({
      message: `Are you sure you want to reprocess the video composition for session "${session.sessionId}"? This will create a new composition using the existing video files.`,
      header: 'Re-Process Video Composition',
      icon: 'pi pi-refresh',
      acceptButtonStyleClass: 'p-button-info',
      accept: () => {
        this.sessionService.reprocessComposition(session.sessionId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (result) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Composition Started',
                detail: 'Video composition reprocessing has been started successfully. You will receive a new composition when complete.'
              });
              
              // Refresh the session data to show the new composition status
              this.loadSessions();
              this.loadStats();
              
              // If the media dialog is open for this session, refresh it
              if (this.showMediaDialog && this.currentSession?.sessionId === session.sessionId) {
                this.viewSessionMedia(session);
              }
            },
            error: (error) => {
              console.error('Error reprocessing composition:', error);
              let errorMessage = 'Failed to start composition reprocessing';
              
              // Extract specific error message if available
              if (error?.error?.message) {
                errorMessage = error.error.message;
              } else if (error?.message) {
                errorMessage = error.message;
              }
              
              this.messageService.add({
                severity: 'error',
                summary: 'Reprocessing Failed',
                detail: errorMessage
              });
            }
          });
      }
    });
  }

  regenerateSessionUrls(session: Session) {
    this.confirmationService.confirm({
      message: `Are you sure you want to regenerate the URLs for session "${session.sessionId}"? This will create new tokens and URLs, invalidating the current ones.`,
      header: 'Regenerate Session URLs',
      icon: 'pi pi-sync',
      acceptButtonStyleClass: 'p-button-info',
      accept: () => {
        this.sessionService.regenerateSessionUrls(session.sessionId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (result) => {
              this.messageService.add({
                severity: 'success',
                summary: 'URLs Regenerated',
                detail: 'Session URLs and tokens have been regenerated successfully.'
              });
              
              // Refresh the sessions list to show the updated URLs
              this.loadSessions();
            },
            error: (error) => {
              console.error('Error regenerating URLs:', error);
              let errorMessage = 'Failed to regenerate session URLs';
              
              // Extract specific error message if available
              if (error?.error?.message) {
                errorMessage = error.error.message;
              } else if (error?.message) {
                errorMessage = error.message;
              }
              
              this.messageService.add({
                severity: 'error',
                summary: 'Regeneration Failed',
                detail: errorMessage
              });
            }
          });
      }
    });
  }

  // Media management methods
  viewSessionMedia(session: Session) {
    console.log('viewSessionMedia called for session:', session.sessionId);
    this.currentSession = session;
    this.mediaLoading = true;
    this.showMediaDialog = true;
    
    // Reset all controls
    this.globalSearchValue = '';
    this.photoSelectAll = false;
    this.selectedPhotoItems.clear();
    this.videoSelectAll = false;
    this.selectedVideoItems.clear();
    
    this.sessionService.getSessionMediaFiles(session.sessionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (mediaFiles) => {
          console.log('Media files received:', mediaFiles);
          this.mediaFiles = mediaFiles;
          this.mediaItems = this.convertMediaFilesToItems(mediaFiles);
          console.log('Converted media items:', this.mediaItems);
          
          // Initialize photo and video filtering
          this.applyPhotoFiltersAndSort();
          this.applyVideoFiltersAndSort();
          
          this.mediaLoading = false;
        },
        error: (error) => {
          console.error('Error loading media files:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load media files'
          });
          this.mediaLoading = false;
        }
      });

    this.sessionService.getSessionCompositions(session.sessionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (compositions) => {
          this.compositions = compositions;
        },
        error: (error) => {
          console.error('Failed to load compositions:', error);
        }
      });
  }

  viewSessionLogs(session: Session) {
    console.log('viewSessionLogs called for session:', session.sessionId);
    this.currentSession = session;
    this.showLogsDialog = true;
  }

  downloadMediaFile(mediaFile: MediaFile) {
    this.sessionService.downloadMediaFile(mediaFile.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = mediaFile.name;
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to download media file'
          });
        }
      });
  }

  downloadComposition(compositionId: string) {
    this.sessionService.downloadComposition(compositionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `composition-${compositionId}.mp4`;
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to download composition'
          });
        }
      });
  }

  downloadSessionMedia(session: Session) {
    // Implementation for bulk download of session media
    this.messageService.add({
      severity: 'info',
      summary: 'Download Started',
      detail: 'Preparing media files for download...'
    });
  }

  // Convert MediaFile[] to MediaItem[] for media gallery
  private convertMediaFilesToItems(mediaFiles: MediaFile[]): MediaItem[] {
    return mediaFiles.map((file, index) => ({
      id: file.id,
      sessionId: file.sessionId,
      name: file.name,
      key: file.key,
      type: file.type,
      size: file.size,
      url: file.url,
      label: file.label,
      sortOrder: file.sortOrder,
      thumbnailUrl: undefined, // MediaFile doesn't have thumbnails yet
      createdAt: new Date(file.createdAt),
      status: 'ready' as const,
      progress: undefined,
      selected: false
    }));
  }

  // Media grid controls
  setMediaGridSize(size: 'small' | 'medium' | 'large') {
    this.mediaGridSize = size;
  }
  
  // Global search
  filterAllMedia() {
    // Global search affects both photo and video filtering
    this.applyPhotoFiltersAndSort();
    this.applyVideoFiltersAndSort();
    // Update select all states since filtered items may have changed
    this.updatePhotoSelectAllState();
    this.updateVideoSelectAllState();
  }
  
  // Media item filtering methods
  getPhotoItems(): MediaItem[] {
    return this.mediaItems.filter(item => item.type?.startsWith('image/') || !item.type?.startsWith('video/'));
  }
  
  getFilteredPhotoItems(): MediaItem[] {
    return this.filteredPhotoItems;
  }
  
  getVideoItems(): MediaItem[] {
    return this.mediaItems.filter(item => item.type?.startsWith('video/'));
  }
  
  getFilteredVideoItems(): MediaItem[] {
    return this.filteredVideoItems;
  }
  
  // Unified media controls
  getVisibleMediaItems(): MediaItem[] {
    return this.selectedMediaType === 'photos' ? this.getFilteredPhotoItems() : this.getFilteredVideoItems();
  }
  
  get currentSortBy(): 'date' | 'name' | 'size' {
    return this.selectedMediaType === 'photos' ? this.photoSortBy : this.videoSortBy;
  }
  
  set currentSortBy(value: 'date' | 'name' | 'size') {
    if (this.selectedMediaType === 'photos') {
      this.photoSortBy = value;
    } else {
      this.videoSortBy = value;
    }
  }
  
  get currentSortOrder(): 'asc' | 'desc' {
    return this.selectedMediaType === 'photos' ? this.photoSortOrder : this.videoSortOrder;
  }
  
  set currentSortOrder(value: 'asc' | 'desc') {
    if (this.selectedMediaType === 'photos') {
      this.photoSortOrder = value;
    } else {
      this.videoSortOrder = value;
    }
  }
  
  get currentSelectAll(): boolean {
    return this.selectedMediaType === 'photos' ? this.photoSelectAll : this.videoSelectAll;
  }
  
  set currentSelectAll(value: boolean) {
    if (this.selectedMediaType === 'photos') {
      this.photoSelectAll = value;
    } else {
      this.videoSelectAll = value;
    }
  }
  
  get currentSelectedItems(): Set<string> {
    return this.selectedMediaType === 'photos' ? this.selectedPhotoItems : this.selectedVideoItems;
  }
  
  get currentBatchMenuItems(): MenuItem[] {
    return this.selectedMediaType === 'photos' ? this.photoBatchMenuItems : this.videoBatchMenuItems;
  }
  
  onMediaTypeChange(): void {
    // Triggered when user switches between photos and videos
    console.log('Media type changed to:', this.selectedMediaType);
    console.log('Total media items:', this.mediaItems.length);
    console.log('Filtered photos:', this.filteredPhotoItems.length);
    console.log('Filtered videos:', this.filteredVideoItems.length);
    console.log('Visible items:', this.getVisibleMediaItems().length);
  }
  
  sortCurrentMedia(): void {
    if (this.selectedMediaType === 'photos') {
      this.sortPhotos();
    } else {
      this.sortVideos();
    }
  }
  
  toggleCurrentSelectAll(): void {
    if (this.selectedMediaType === 'photos') {
      this.togglePhotoSelectAll();
    } else {
      this.toggleVideoSelectAll();
    }
  }
  
  onCurrentSelectionChanged(selectedIds: Set<string>): void {
    if (this.selectedMediaType === 'photos') {
      this.onPhotoSelectionChanged(selectedIds);
    } else {
      this.onVideoSelectionChanged(selectedIds);
    }
  }
  
  // Photo section controls
  togglePhotoSelectAll() {
    const allSelected = this.selectedPhotoItems.size === this.getFilteredPhotoItems().length && this.getFilteredPhotoItems().length > 0;
    
    if (allSelected) {
      // If all items are currently selected, deselect all
      this.selectedPhotoItems = new Set(); // Create new Set to trigger change detection
      this.photoSelectAll = false;
    } else {
      // If not all items are selected, select all
      this.selectedPhotoItems = new Set(this.getFilteredPhotoItems().map(item => item.id));
      this.photoSelectAll = true;
    }
  }
  
  sortPhotos() {
    console.log('sortPhotos called - sortBy:', this.photoSortBy, 'sortOrder:', this.photoSortOrder);
    this.applyPhotoFiltersAndSort();
    this.updatePhotoSelectAllState();
  }
  
  private applyPhotoFiltersAndSort() {
    let photos = [...this.getPhotoItems()]; // Create new array with spread operator
    
    // Apply global search filter
    if (this.globalSearchValue) {
      const globalFilter = this.globalSearchValue.toLowerCase();
      photos = photos.filter(item => 
        item.name.toLowerCase().includes(globalFilter) ||
        item.label?.toLowerCase().includes(globalFilter)
      );
    }
    
    // Apply sort
    photos.sort((a, b) => {
      let valueA: any, valueB: any;
      
      switch (this.photoSortBy) {
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'date':
          valueA = new Date(a.createdAt).getTime();
          valueB = new Date(b.createdAt).getTime();
          break;
        case 'size':
          valueA = a.size;
          valueB = b.size;
          break;
        default:
          return 0;
      }
      
      const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      return this.photoSortOrder === 'asc' ? comparison : -comparison;
    });
    
    this.filteredPhotoItems = [...photos]; // Create new array reference
    console.log('Filtered photo items updated:', this.filteredPhotoItems.length, 'items');
    
    // Update select all state after filtering/sorting
    this.updatePhotoSelectAllState();
  }
  
  // Video section controls
  toggleVideoSelectAll() {
    const allSelected = this.selectedVideoItems.size === this.getFilteredVideoItems().length && this.getFilteredVideoItems().length > 0;
    
    if (allSelected) {
      // If all items are currently selected, deselect all
      this.selectedVideoItems = new Set(); // Create new Set to trigger change detection
      this.videoSelectAll = false;
    } else {
      // If not all items are selected, select all
      this.selectedVideoItems = new Set(this.getFilteredVideoItems().map(item => item.id));
      this.videoSelectAll = true;
    }
  }
  
  sortVideos() {
    console.log('sortVideos called - sortBy:', this.videoSortBy, 'sortOrder:', this.videoSortOrder);
    this.applyVideoFiltersAndSort();
    this.updateVideoSelectAllState();
  }
  
  private applyVideoFiltersAndSort() {
    let videos = [...this.getVideoItems()]; // Create new array with spread operator
    
    // Apply global search filter
    if (this.globalSearchValue) {
      const globalFilter = this.globalSearchValue.toLowerCase();
      videos = videos.filter(item => 
        item.name.toLowerCase().includes(globalFilter) ||
        item.label?.toLowerCase().includes(globalFilter)
      );
    }
    
    // Apply sort
    videos.sort((a, b) => {
      let valueA: any, valueB: any;
      
      switch (this.videoSortBy) {
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'date':
          valueA = new Date(a.createdAt).getTime();
          valueB = new Date(b.createdAt).getTime();
          break;
        case 'size':
          valueA = a.size;
          valueB = b.size;
          break;
        default:
          return 0;
      }
      
      const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      return this.videoSortOrder === 'asc' ? comparison : -comparison;
    });
    
    this.filteredVideoItems = [...videos]; // Create new array reference
    console.log('Filtered video items updated:', this.filteredVideoItems.length, 'items');
    
    // Update select all state after filtering/sorting
    this.updateVideoSelectAllState();
  }

  // Selection state management
  updatePhotoSelectAllState() {
    const photoItems = this.getFilteredPhotoItems();
    if (photoItems.length === 0) {
      this.photoSelectAll = false;
      return;
    }
    
    const selectedCount = photoItems.filter(item => this.selectedPhotoItems.has(item.id)).length;
    this.photoSelectAll = selectedCount === photoItems.length;
  }
  
  updateVideoSelectAllState() {
    const videoItems = this.getFilteredVideoItems();
    if (videoItems.length === 0) {
      this.videoSelectAll = false;
      return;
    }
    
    const selectedCount = videoItems.filter(item => this.selectedVideoItems.has(item.id)).length;
    this.videoSelectAll = selectedCount === videoItems.length;
  }
  
  // Individual item selection handlers
  onPhotoItemSelected(itemId: string, selected: boolean) {
    if (selected) {
      this.selectedPhotoItems.add(itemId);
    } else {
      this.selectedPhotoItems.delete(itemId);
    }
    this.updatePhotoSelectAllState();
  }
  
  onVideoItemSelected(itemId: string, selected: boolean) {
    if (selected) {
      this.selectedVideoItems.add(itemId);
    } else {
      this.selectedVideoItems.delete(itemId);
    }
    this.updateVideoSelectAllState();
  }

  // Selection change handlers
  onPhotoSelectionChanged(selectedIds: Set<string>) {
    this.selectedPhotoItems = new Set(selectedIds);
    this.updatePhotoSelectAllState();
  }

  onVideoSelectionChanged(selectedIds: Set<string>) {
    this.selectedVideoItems = new Set(selectedIds);
    this.updateVideoSelectAllState();
  }

  // Media gallery event handlers
  onMediaDeleted(mediaId: string) {
    // Remove from both arrays
    this.mediaFiles = this.mediaFiles.filter(m => m.id !== mediaId);
    this.mediaItems = this.mediaItems.filter(m => m.id !== mediaId);
    
    // Remove from selection sets
    this.selectedPhotoItems.delete(mediaId);
    this.selectedVideoItems.delete(mediaId);
    
    // Update filtered videos if needed
    this.applyVideoFiltersAndSort();
    
    // Update select all states
    this.updatePhotoSelectAllState();
    this.updateVideoSelectAllState();
    
    this.messageService.add({
      severity: 'success',
      summary: 'Media Deleted',
      detail: 'Media file has been deleted successfully'
    });
    
    // Reload session stats to reflect changes
    this.loadStats();
  }

  onMediaUpdated(updatedMedia: MediaItem) {
    // Update in mediaItems array
    const index = this.mediaItems.findIndex(m => m.id === updatedMedia.id);
    if (index !== -1) {
      this.mediaItems[index] = updatedMedia;
    }
    
    this.messageService.add({
      severity: 'success',
      summary: 'Media Updated',
      detail: 'Media file has been updated successfully'
    });
  }

  // Bulk operations
  bulkDelete() {
    if (this.selectedSessions.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select sessions to delete'
      });
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${this.selectedSessions.length} selected sessions? This action cannot be undone.`,
      header: 'Delete Sessions',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const sessionIds = this.selectedSessions.map(s => s.sessionId);
        this.sessionService.deleteSessions(sessionIds)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `${sessionIds.length} sessions deleted successfully`
              });
              this.selectedSessions = [];
              this.loadSessions();
              this.loadStats();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete selected sessions'
              });
            }
          });
      }
    });
  }

  bulkDownload() {
    if (this.selectedSessions.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select sessions to download'
      });
      return;
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Download Started',
      detail: `Preparing ${this.selectedSessions.length} sessions for download...`
    });
  }

  // Menu item handlers
  showActionMenu(event: any, session: Session) {
    this.currentSession = session;
    this.actionMenu.show(event);
  }

  // Utility methods
  formatFileSize(bytes: number): string {
    return this.sessionService.formatFileSize(bytes);
  }

  getRoleLabel(role: number): string {
    return this.sessionService.getRoleLabel(role);
  }

  getStatusLabel(composition: Composition): string {
    return this.sessionService.getStatusLabel(composition);
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'processing': return 'info';
      case 'failed': return 'danger';
      case 'pending': return 'warn';
      default: return 'secondary';
    }
  }

  // Dialog event handlers
  onSessionSaved(sessionResponse: SessionCreateResponse) {
    // Show success message with session details
    this.messageService.add({
      severity: 'info',
      summary: 'Session Created',
      detail: `Session '${sessionResponse.sessionId}' is ready! Host and guest URLs have been generated.`,
      life: 5000
    });
    
    this.loadSessions();
    this.loadStats();
  }

  // Statistics methods
  showStatistics() {
    this.showStatsDialog = true;
  }

  // Batch operation methods
  setupPhotoBatchMenuItems() {
    this.photoBatchMenuItems = [
      {
        label: 'Download Selected',
        icon: 'pi pi-download',
        command: () => this.downloadSelectedPhotos()
      },
      {
        separator: true
      },
      {
        label: 'Delete Selected',
        icon: 'pi pi-trash',
        command: () => this.deleteSelectedPhotos(),
        styleClass: 'text-red-500'
      }
    ];
  }

  setupVideoBatchMenuItems() {
    this.videoBatchMenuItems = [
      {
        label: 'Download Selected',
        icon: 'pi pi-download',
        command: () => this.downloadSelectedVideos()
      },
      {
        separator: true
      },
      {
        label: 'Delete Selected',
        icon: 'pi pi-trash',
        command: () => this.deleteSelectedVideos(),
        styleClass: 'text-red-500'
      }
    ];
  }

  async downloadSelectedPhotos() {
    const selectedMedia = this.getPhotoItems().filter(item => this.selectedPhotoItems.has(item.id));
    if (selectedMedia.length === 0) return;

    try {
      // Open each file in a new tab with a small delay to prevent popup blocking
      for (let i = 0; i < selectedMedia.length; i++) {
        const item = selectedMedia[i];
        const downloadUrl = await this.downloadService.getOjectDetails(item.key);
        if (downloadUrl?.url) {
          window.open(downloadUrl.url, '_blank', 'noopener,noreferrer');
          // Add small delay between opening tabs to prevent browser blocking
          if (i < selectedMedia.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `Opening ${selectedMedia.length} photo(s) in new tabs`
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to open photos'
      });
    }
  }

  async downloadSelectedVideos() {
    const selectedMedia = this.getVideoItems().filter(item => this.selectedVideoItems.has(item.id));
    if (selectedMedia.length === 0) return;

    try {
      // Open each file in a new tab with a small delay to prevent popup blocking
      for (let i = 0; i < selectedMedia.length; i++) {
        const item = selectedMedia[i];
        const downloadUrl = await this.downloadService.getOjectDetails(item.key);
        if (downloadUrl?.url) {
          window.open(downloadUrl.url, '_blank', 'noopener,noreferrer');
          // Add small delay between opening tabs to prevent browser blocking
          if (i < selectedMedia.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `Opening ${selectedMedia.length} video(s) in new tabs`
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to open videos'
      });
    }
  }

  deleteSelectedPhotos() {
    const selectedMedia = this.getPhotoItems().filter(item => this.selectedPhotoItems.has(item.id));
    if (selectedMedia.length === 0) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${selectedMedia.length} selected photo(s)?`,
      header: 'Delete Photos',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const mediaIds = selectedMedia.map(item => item.id);
          const success = await this.mediaService.batchDeleteMedia(mediaIds);
          
          if (success) {
            // Remove from local arrays
            this.mediaFiles = this.mediaFiles.filter(item => !this.selectedPhotoItems.has(item.id));
            this.mediaItems = this.mediaItems.filter(item => !this.selectedPhotoItems.has(item.id));
            this.selectedPhotoItems.clear();
            this.photoSelectAll = false;
            
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `${selectedMedia.length} photo(s) deleted successfully`
            });
            
            // Refresh the view
            this.applyPhotoFiltersAndSort();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete photos'
            });
          }
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete photos'
          });
        }
      }
    });
  }

  deleteSelectedVideos() {
    const selectedMedia = this.getVideoItems().filter(item => this.selectedVideoItems.has(item.id));
    if (selectedMedia.length === 0) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${selectedMedia.length} selected video(s)?`,
      header: 'Delete Videos',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const mediaIds = selectedMedia.map(item => item.id);
          const success = await this.mediaService.batchDeleteMedia(mediaIds);
          
          if (success) {
            // Remove from local arrays
            this.mediaFiles = this.mediaFiles.filter(item => !this.selectedVideoItems.has(item.id));
            this.mediaItems = this.mediaItems.filter(item => !this.selectedVideoItems.has(item.id));
            this.selectedVideoItems.clear();
            this.videoSelectAll = false;
            
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `${selectedMedia.length} video(s) deleted successfully`
            });
            
            // Refresh the view
            this.applyVideoFiltersAndSort();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete videos'
            });
          }
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete videos'
          });
        }
      }
    });
  }
}
